
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "../../utills/api";
import { io } from "socket.io-client";

export const NotificationContext = createContext();

export const useNotifications = () =>
  useContext(NotificationContext);

// ---------------- ENV DETECTION ----------------
const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const SOCKET_URL = isLocal
  ? "http://localhost:5002"
  : `${window.location.protocol}//${window.location.host}`;

const API_BASE = isLocal
  ? "http://localhost:5002/api"
  : "/api";

const POLL_INTERVAL = 15000;
const POPUP_DURATION = 6000;

const normalizeNotification = (notification) => ({
  ...notification,
  isRead: Boolean(
    notification?.isRead ?? notification?.read ?? false
  ),
  createdAt: notification?.createdAt || new Date().toISOString(),
});

const getNotificationId = (notification) =>
  notification?._id == null
    ? null
    : String(notification._id);

// Supports common API response formats.
const unwrapNotifications = (response) => {
  let data = response?.data?.data ?? response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.notifications)) {
    return data.notifications;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.data?.notifications)) {
    return data.data.notifications;
  }

  console.warn(
    "Unexpected notifications API response:",
    response?.data
  );

  return [];
};

// ---------------- POPUP COMPONENT ----------------
const NotificationToastContainer = ({
  popups,
  dismissPopup,
}) => {
  if (!popups.length) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: "fixed",
        top: "80px",
        right: "20px",
        zIndex: 99999,
        width: "min(370px, calc(100vw - 32px))",
        pointerEvents: "none",
      }}
    >
      {popups.map((popup) => (
        <div
          key={popup._id}
          role="status"
          style={{
            background: "#ffffff",
            color: "#212529",
            border: "1px solid #dee2e6",
            borderLeft: "4px solid #0d6efd",
            borderRadius: "8px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
            padding: "14px 16px",
            marginBottom: "10px",
            pointerEvents: "auto",
            animation: "notificationSlideIn 0.2s ease-out",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <strong>{popup.title}</strong>

            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismissPopup(popup._id)}
              style={{
                border: 0,
                background: "transparent",
                fontSize: "22px",
                cursor: "pointer",
                lineHeight: 1,
                color: "#6c757d",
              }}
            >
              ×
            </button>
          </div>

          <div
            style={{
              marginTop: "8px",
              fontSize: "14px",
              overflowWrap: "anywhere",
            }}
          >
            {popup.message}
          </div>

          <small
            style={{
              display: "block",
              marginTop: "8px",
              color: "#6c757d",
            }}
          >
            {new Date(popup.createdAt).toLocaleTimeString()}
          </small>
        </div>
      ))}

      <style>
        {`
          @keyframes notificationSlideIn {
            from {
              opacity: 0;
              transform: translateX(12px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>
    </div>
  );
};

// ---------------- PROVIDER ----------------
export const NotificationProvider = ({
  children,
  userId,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  const [globalPopup, setGlobalPopup] = useState({
    show: false,
    message: "",
  });

  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const userIdRef = useRef(userId);
  const popupTimersRef = useRef(new Map());
  const globalPopupTimerRef = useRef(null);
  const requestInProgressRef = useRef(false);

  // ---------------- USER CHANGE ----------------
  useEffect(() => {
    userIdRef.current = userId;
    seenIdsRef.current = new Set();
    initializedRef.current = false;
    requestInProgressRef.current = false;

    setNotifications([]);
    setPopups([]);
    setGlobalPopup({
      show: false,
      message: "",
    });
    setLoading(true);
  }, [userId]);

  // ---------------- AUDIO SETUP ----------------
  useEffect(() => {
    const audio = new Audio("/notification.mp3");
    audio.preload = "auto";
    audioRef.current = audio;

    const unlockAudio = async () => {
      if (audioUnlockedRef.current || !audioRef.current) {
        return;
      }

      try {
        const currentAudio = audioRef.current;
        currentAudio.muted = true;
        currentAudio.currentTime = 0;

        await currentAudio.play();

        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio.muted = false;

        audioUnlockedRef.current = true;
        console.info("Notification audio enabled.");
      } catch (error) {
        if (audioRef.current) {
          audioRef.current.muted = false;
        }

        // Audio can remain blocked until a later user interaction.
        console.warn(
          "Notification audio is not unlocked yet:",
          error?.message || error
        );
      }
    };

    document.addEventListener("click", unlockAudio);
    document.addEventListener("keydown", unlockAudio);

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);

      audio.pause();
      audioRef.current = null;
    };
  }, []);

  // ---------------- PLAY SOUND ----------------
  const playSound = useCallback(() => {
    const audio = audioRef.current;

    if (!audio) {
      console.warn("Notification audio is not initialized.");
      return;
    }

    try {
      audio.currentTime = 0;
      audio.muted = false;

      const playPromise = audio.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.warn(
            "Notification sound failed. Check browser audio permissions and public/notification.mp3:",
            error?.message || error
          );
        });
      }
    } catch (error) {
      console.error("Notification sound error:", error);
    }
  }, []);

  // ---------------- POPUPS ----------------
  const dismissPopup = useCallback((popupId) => {
    const id = String(popupId);
    const timer = popupTimersRef.current.get(id);

    if (timer) {
      clearTimeout(timer);
      popupTimersRef.current.delete(id);
    }

    setPopups((previous) =>
      previous.filter((popup) => String(popup._id) !== id)
    );
  }, []);

  const showPopup = useCallback(
    (notification) => {
      const id =
        getNotificationId(notification) ||
        `local-${Date.now()}-${Math.random()}`;

      const popup = {
        _id: id,
        title: notification.title || "New notification",
        message: notification.message || "",
        createdAt: notification.createdAt || new Date().toISOString(),
      };

      // Don't display the same notification twice.
      setPopups((previous) => {
        if (
          previous.some(
            (item) => String(item._id) === String(id)
          )
        ) {
          return previous;
        }

        return [popup, ...previous];
      });

      const existingTimer = popupTimersRef.current.get(String(id));

      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        setPopups((previous) =>
          previous.filter(
            (item) => String(item._id) !== String(id)
          )
        );

        popupTimersRef.current.delete(String(id));
      }, POPUP_DURATION);

      popupTimersRef.current.set(String(id), timer);
    },
    []
  );

  const showGlobalPopup = useCallback((notification) => {
    setGlobalPopup({
      show: true,
      message:
        notification.message ||
        notification.title ||
        "You have a new notification",
    });

    if (globalPopupTimerRef.current) {
      clearTimeout(globalPopupTimerRef.current);
    }

    globalPopupTimerRef.current = setTimeout(() => {
      setGlobalPopup({
        show: false,
        message: "",
      });

      globalPopupTimerRef.current = null;
    }, POPUP_DURATION);
  }, []);

  const closeGlobalPopup = useCallback(() => {
    if (globalPopupTimerRef.current) {
      clearTimeout(globalPopupTimerRef.current);
      globalPopupTimerRef.current = null;
    }

    setGlobalPopup({
      show: false,
      message: "",
    });
  }, []);

  // ---------------- HANDLE INCOMING ----------------
  const handleIncomingNotification = useCallback(
    (rawNotification, { notify = true } = {}) => {
      const notification = normalizeNotification(rawNotification);
      const id = getNotificationId(notification);

      if (id && seenIdsRef.current.has(id)) {
        // Update an existing notification without repeating its alert.
        setNotifications((previous) =>
          previous.map((item) =>
            getNotificationId(item) === id
              ? { ...item, ...notification }
              : item
          )
        );

        return;
      }

      if (id) {
        seenIdsRef.current.add(id);
      }

      setNotifications((previous) => {
        if (
          id &&
          previous.some(
            (item) => getNotificationId(item) === id
          )
        ) {
          return previous.map((item) =>
            getNotificationId(item) === id
              ? { ...item, ...notification }
              : item
          );
        }

        return [notification, ...previous];
      });

      if (notify) {
        console.info(
          "New notification received:",
          notification.title,
          id
        );

        showPopup(notification);
        showGlobalPopup(notification);
        playSound();
      }
    },
    [playSound, showPopup, showGlobalPopup]
  );

  // ---------------- FETCH NOTIFICATIONS ----------------
  const fetchNotifications = useCallback(
    async ({ notifyNew = false } = {}) => {
      if (!userIdRef.current) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      // Prevent overlapping polling and socket-triggered requests.
      if (requestInProgressRef.current) {
        return;
      }

      requestInProgressRef.current = true;

      try {
        const response = await axios.get(
          `${API_BASE}/notifications`
        );

        const items = unwrapNotifications(response)
          .map(normalizeNotification)
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() -
              new Date(b.createdAt).getTime()
          );

        // First successful fetch establishes the baseline.
        // Existing notifications won't all alert on page load.
        if (!initializedRef.current) {
          seenIdsRef.current = new Set(
            items
              .map(getNotificationId)
              .filter(Boolean)
          );

          setNotifications([...items].reverse());
          initializedRef.current = true;

          console.info(
            `Notification baseline loaded: ${items.length} notification(s).`
          );

          return;
        }

        const newlyArrived = notifyNew
          ? items.filter((item) => {
            const id = getNotificationId(item);

            return id && !seenIdsRef.current.has(id);
          })
          : [];

        // Mark before handling, so Socket.IO and polling cannot
        // trigger duplicate sounds for the same notification.

        newlyArrived.forEach((item) => {
          // Let handleIncomingNotification mark the ID as seen.
          // Marking it here first suppresses the popup and sound.
          handleIncomingNotification(item, {
            notify: true,
          });
        });
        // Merge server data with local state.
        setNotifications((previous) => {
          const previousById = new Map(
            previous
              .filter((item) => getNotificationId(item))
              .map((item) => [
                getNotificationId(item),
                item,
              ])
          );

          const merged = items.map((item) => ({
            ...previousById.get(getNotificationId(item)),
            ...item,
          }));

          const fetchedIds = new Set(
            merged
              .map(getNotificationId)
              .filter(Boolean)
          );

          const localOnly = previous.filter((item) => {
            const id = getNotificationId(item);

            return !id || !fetchedIds.has(id);
          });

          return [...localOnly, ...merged].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() -
              new Date(a.createdAt).getTime()
          );
        });

        // Track fetched IDs after processing new arrivals.
        items.forEach((item) => {
          const id = getNotificationId(item);

          if (id) {
            seenIdsRef.current.add(id);
          }
        });
      } catch (error) {
        console.error(
          "Error fetching notifications:",
          error?.response?.data || error?.message || error
        );
      } finally {
        requestInProgressRef.current = false;
        setLoading(false);
      }
    },
    [handleIncomingNotification]
  );

  // ---------------- POLLING ----------------
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }

    fetchNotifications({ notifyNew: false });

    const interval = setInterval(() => {
      fetchNotifications({ notifyNew: true });
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  // ---------------- SOCKET CONNECTION ----------------

  // ---------------- SOCKET CONNECTION ----------------
  useEffect(() => {
    if (!userId) return undefined;

    const token = localStorage.getItem("token");

    if (!token) {
      console.warn(
        "[Notifications] Cannot connect: authentication token missing."
      );
      return undefined;
    }

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    setSocket(newSocket);

    const onConnect = () => {
      console.info(
        "[Notifications] Live connection established:",
        newSocket.id
      );

      fetchNotifications({ notifyNew: true });
    };

    const onConnectError = (error) => {
      console.error(
        "[Notifications] Socket connection failed:",
        error.message
      );
    };

    newSocket.on("connect", onConnect);
    newSocket.on("connect_error", onConnectError);

    return () => {
      newSocket.off("connect", onConnect);
      newSocket.off("connect_error", onConnectError);
      newSocket.disconnect();

      setSocket((current) =>
        current === newSocket ? null : current
      );
    };
  }, [userId, fetchNotifications]);


  // ---------------- SOCKET LISTENER ----------------
  useEffect(() => {
    if (!socket || !userId) return undefined;

    const onNewNotification = (rawNotification) => {
      const notification = normalizeNotification(rawNotification);

      const notificationUserId =
        notification.userId?._id ??
        notification.userId;

      // Ignore events explicitly addressed to a different user.
      // Events without a recipient ID are accepted because some
      // backends emit the notification payload without userId.
      if (
        notificationUserId != null &&
        String(notificationUserId) !== String(userId) &&
        !notification.toAdmins
      ) {
        return;
      }

      handleIncomingNotification(notification, {
        notify: true,
      });
    };

    socket.on("newNotification", onNewNotification);

    return () => {
      socket.off("newNotification", onNewNotification);
    };
  }, [socket, userId, handleIncomingNotification]);

  // ---------------- ADD LOCAL NOTIFICATION ----------------
  const addNotification = useCallback(
    ({ title, message, type, ...extra }) => {
      const notification = {
        ...extra,
        _id: `local-${Date.now()}-${Math.random()}`,
        title,
        message,
        type,
        isRead: false,
        createdAt: new Date().toISOString(),
      };

      handleIncomingNotification(notification, {
        notify: true,
      });
    },
    [handleIncomingNotification]
  );

  // ---------------- MARK ONE AS READ ----------------
  const markAsRead = useCallback(async (notificationId) => {
    const id = String(notificationId);

    if (id.startsWith("local-")) {
      setNotifications((previous) =>
        previous.map((item) =>
          String(item._id) === id
            ? { ...item, isRead: true }
            : item
        )
      );

      return;
    }

    const previousNotifications = notifications;

    setNotifications((previous) =>
      previous.map((item) =>
        String(item._id) === id
          ? { ...item, isRead: true }
          : item
      )
    );

    try {
      await axios.put(`${API_BASE}/notifications/${id}/read`);
    } catch (error) {
      setNotifications(previousNotifications);

      console.error(
        "Error marking notification as read:",
        error?.response?.data || error?.message || error
      );
    }
  }, [notifications]);

  // ---------------- MARK ALL AS READ ----------------
  const deleteAllNotifications = useCallback(async () => {
    const previousNotifications = notifications;

    setNotifications((previous) =>
      previous.map((item) => ({
        ...item,
        isRead: true,
      }))
    );

    try {
      await axios.put(`${API_BASE}/notifications/read-all`);
    } catch (error) {
      setNotifications(previousNotifications);

      console.error(
        "Error marking all notifications as read:",
        error?.response?.data || error?.message || error
      );
    }
  }, [notifications]);

  // ---------------- UNREAD COUNT ----------------
  const unreadCount = notifications.filter(
    (notification) => !notification.isRead
  ).length;

  // ---------------- CLEANUP TIMERS ----------------
  useEffect(() => {
    return () => {
      popupTimersRef.current.forEach((timer) => clearTimeout(timer));
      popupTimersRef.current.clear();

      if (globalPopupTimerRef.current) {
        clearTimeout(globalPopupTimerRef.current);
      }
    };
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loading,
        socket,
        addNotification,
        markAsRead,
        deleteAllNotifications,
        popups,
        dismissPopup,
        unreadCount,
        globalPopup,
        closeGlobalPopup,
        fetchNotifications,
      }}
    >
      {children}

      <NotificationToastContainer
        popups={popups}
        dismissPopup={dismissPopup}
      />
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
